import { Alert, AlertDescription, AlertTitle, Spinner } from '@folio/components';
import { cn } from '@folio/lib';
import { AlertCircle } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

const PDFDocument = lazy(() => import('./PDFDocument'));

interface PDFViewerProps {
  url: string;
  className?: string;
}

export function PDFViewer({ url, className }: PDFViewerProps) {
  const { t } = useTranslation();
  const loading = (
    <Stack align="center" justify="center" className="min-h-[300px]">
      <Spinner className="h-8 w-8" />
      <Text size="sm" tone="muted">
        {t('pdfViewer.loading')}
      </Text>
    </Stack>
  );
  const error = (
    <Alert variant="destructive" className="m-4 w-auto">
      <AlertCircle className="h-5 w-5" />
      <AlertTitle>{t('pdfViewer.error.title')}</AlertTitle>
      <AlertDescription>{t('pdfViewer.error.description')}</AlertDescription>
    </Alert>
  );

  return (
    <div className={cn('h-full w-full min-h-0', className)}>
      <ErrorBoundary fallback={error} resetKeys={[url]}>
        <Suspense fallback={loading}>
          <PDFDocument key={url} url={url} loading={loading} error={error} />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}
